import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useInView } from "framer-motion";
import { Download, Zap, ArrowDown } from "lucide-react";
import PhoneDemo from "./PhoneDemo";
import { scrollToSection } from "./Navbar";

const ease = [0.16, 1, 0.3, 1];

const rotatingWords = ["a perfect ride.", "a great order.", "a busy hour.", "your best trip."];

const Line = ({ children, delay = 0 }) => (
    <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, delay, ease }}
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero() {
    const ref = useRef(null);
    const [wordIndex, setWordIndex] = useState(0);
    const revealed = useRef(false);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const rotate = useTransform(scrollYProgress, [0, 1], [0, 5]);
    const isDesktop =
        typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;

    const heroInView = useInView(ref, { margin: "-10% 0px" });

    useEffect(() => {
        if (!heroInView) return;
        const id = setInterval(() => {
            revealed.current = true;
            setWordIndex((v) => (v + 1) % rotatingWords.length);
        }, 2800);
        return () => clearInterval(id);
    }, [heroInView]);

    return (
        <section
            ref={ref}
            id="hero"
            data-testid="hero-section"
            className="relative overflow-hidden pb-14 pt-24 lg:pb-32 lg:pt-44"
        >
            <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:px-10">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.15, ease }}
                        className="mb-8 inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-1.5"
                        data-testid="hero-badge"
                    >
                        <Zap size={13} className="text-brand" />
                        <span className="whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.18em] text-mute sm:text-[11px]">
                            The ultimate tool for captains
                        </span>
                    </motion.div>

                    <h1
                        data-testid="hero-title"
                        className="font-display text-[13.5vw] font-bold leading-[0.98] tracking-tight text-ink sm:text-7xl lg:text-[4.6rem] xl:text-[5.4rem]"
                    >
                        <Line delay={0.25}>Drive safe.</Line>
                        <Line delay={0.37}>Never miss</Line>
                        <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
                            <span className="relative block h-[1.05em] text-brand">
                                <AnimatePresence>
                                    <motion.span
                                        key={wordIndex}
                                        data-testid="hero-rotating-word"
                                        initial={{ y: "115%", opacity: 0 }}
                                        animate={{
                                            y: "0%",
                                            opacity: 1,
                                            transition: {
                                                duration: revealed.current ? 0.55 : 1.1,
                                                delay: revealed.current ? 0 : 0.49,
                                                ease,
                                            },
                                        }}
                                        exit={{
                                            y: "-115%",
                                            opacity: 0,
                                            transition: { duration: 0.55, ease },
                                        }}
                                        className="absolute inset-0"
                                    >
                                        {rotatingWords[wordIndex]}
                                    </motion.span>
                                </AnimatePresence>
                            </span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.72, ease }}
                        className="mt-8 max-w-md text-base leading-relaxed text-mute md:text-lg"
                        data-testid="hero-subtitle"
                    >
                        Set your rules once and let RapidClick secure the best trips for you in
                        milliseconds. Stop staring at your phone, stay safe on the road, and watch
                        your daily earnings grow — completely hands-free.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.88, ease }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-download-btn"
                            onClick={(e) => scrollToSection(e, "#download")}
                            className="group inline-flex items-center gap-2.5 rounded-full bg-ink px-7 py-4 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.04]"
                        >
                            <Download
                                size={16}
                                className="transition-transform duration-300 group-hover:translate-y-0.5"
                            />
                            Download App
                        </button>
                        <button
                            data-testid="hero-demo-btn"
                            onClick={(e) => scrollToSection(e, "#how")}
                            className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-7 py-4 text-sm font-bold text-ink transition-colors duration-300 hover:border-ink/40"
                        >
                            See how it works <ArrowDown size={15} />
                        </button>
                    </motion.div>
                </div>

                <motion.div
                    style={isDesktop ? { y, rotate } : {}}
                    className="relative flex justify-center lg:justify-end"
                >
                    <div className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[440px] w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/10 blur-[110px] md:block" />
                    <motion.div
                        animate={
                            typeof window !== "undefined" &&
                            window.matchMedia("(max-width: 768px)").matches
                                ? {}
                                : { y: [0, -12, 0] }
                        }
                        transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                        className="relative"
                    >
                        <PhoneDemo />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
